import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import jsQR from 'jsqr';
import { Camera, CameraOff, Check, RotateCcw, Search, WifiOff } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import { dateTime, money } from '@/lib/format';
import { Avatar } from '@/components/Photo';
import { Emblem } from '@/components/Brand';
import {
  bookingState, enqueue, findByCode, loadCachedList, loadQueue, parseCode, reasonText, saveCachedList, saveQueue, searchDesk, withQueue,
  type DeskBooking, type DeskList, type DeskMember, type Found, type QueuedCheckin,
} from '@/lib/desk';

const time = (iso: string) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const band = (b: string | null) => b ? b[0]!.toUpperCase() + b.slice(1) : '';

/** A short beep and buzz when a code is read, so the helper need not watch the screen. */
function signal() {
  try { navigator.vibrate?.(60); } catch { /* not on iPhone */ }
  try {
    const ctx = new AudioContext(); const o = ctx.createOscillator(); const g = ctx.createGain();
    o.frequency.value = 880; g.gain.value = 0.08; o.connect(g); g.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.09);
  } catch { /* no sound: fine */ }
}

/**
 * The check-in desk for one event, for admins and that event's desk helpers. Loads the
 * event's bookings and members once, so scanning, searching and plain check-ins work
 * without signal (they queue and send when it returns). Taking money and booking
 * walk-ins need signal.
 */
export function Desk() {
  const { id: eventId = '' } = useParams();
  const [list, setList] = useState<DeskList | null>(() => loadCachedList(eventId));
  const [queue, setQueue] = useState<QueuedCheckin[]>(() => loadQueue(eventId));
  const [online, setOnline] = useState(navigator.onLine);
  const [error, setError] = useState('');
  const [denied, setDenied] = useState(false);
  const [found, setFound] = useState<Found | null>(null);
  const [q, setQ] = useState('');
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [tab, setTab] = useState<'waiting' | 'arrived'>('waiting');
  const inputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    const { data, error } = await supabase.rpc('desk_list', { p_event: eventId });
    if (error) {
      if (/not_on_desk/.test(error.message)) setDenied(true);
      else if (!loadCachedList(eventId)) setError('Could not load the desk. Check the signal and reload.');
      return;
    }
    setList(data as DeskList); saveCachedList(eventId, data as DeskList); setError('');
  }, [eventId]);

  // Send check-ins made without signal.
  const flush = useCallback(async () => {
    const pending = loadQueue(eventId);
    if (!pending.length || !navigator.onLine) return;
    const { error } = await supabase.rpc('desk_checkin', { p_event: eventId, p_items: pending });
    if (!error) { saveQueue(eventId, []); setQueue([]); await refresh(); }
  }, [eventId, refresh]);

  useEffect(() => { void refresh().then(flush); }, [refresh, flush]);
  useEffect(() => {
    const on = () => { setOnline(true); void flush(); }; const off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    const t = setInterval(() => { void flush(); }, 20_000);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); clearInterval(t); };
  }, [flush]);

  const shown = useMemo(() => list && withQueue(list, queue, 'you'), [list, queue]);
  const say = (m: string) => { setToast(m); setTimeout(() => setToast(t => t === m ? '' : t), 3500); };
  // Keep the open card in step with the list after an action.
  const current: Found | null = useMemo(() => {
    if (!found || !shown) return found;
    if (found.kind === 'booking') { const b = shown.bookings.find(x => x.id === found.booking.id); return b ? { kind: 'booking', booking: b } : found; }
    if (found.kind === 'member') { const b = shown.bookings.find(x => x.person_id === found.member.person_id); return b ? { kind: 'booking', booking: b } : found; }
    return found;
  }, [found, shown]);

  const onCode = useCallback((text: string) => {
    if (!shown) return;
    const code = parseCode(text);
    if (!code) { setQ(text); return; }
    signal(); setQ(''); setFound(findByCode(shown, code));
  }, [shown]);

  async function checkIn(b: DeskBooking) {
    const next = enqueue(loadQueue(eventId), b.id);
    saveQueue(eventId, next); setQueue(next); setFound(null);
    say(`${b.name} checked in${navigator.onLine ? '' : ' (will send when there is signal)'}`);
    await flush();
  }
  /** Runs a desk action; `emailFor` names the booking whose household is emailed afterwards (receipt, still to pay, or booked). */
  async function act<T>(run: () => PromiseLike<{ data: T; error: { message: string } | null }>, done: string, emailFor?: (data: T) => string | null) {
    setBusy(true); setError('');
    const { data, error } = await run();
    setBusy(false);
    const reg = !error && emailFor ? emailFor(data) : null;
    // The email never holds up the queue; if it fails, the payment or booking still stands.
    if (reg) void api('/desk/notify', { method: 'POST', body: { registration_id: reg } }).catch(() => undefined);
    if (error) { setError(/not_eligible:(\w+)/.test(error.message) ? reasonText(error.message.match(/not_eligible:(\w+)/)![1]!) : /nothing_owed/.test(error.message) ? 'Nothing is owed on this booking.' : 'That did not work. Check the signal and try again.'); return; }
    setFound(null); say(done); await refresh();
  }

  if (denied) return <Shell title="Check-in desk"><p className="card">You are not on the desk for this event. Ask an admin to add you as a desk helper.</p></Shell>;
  if (!shown) return <Shell title="Check-in desk"><p className="card">{error || 'Loading…'}</p></Shell>;

  const arrived = shown.bookings.filter(b => b.checked_in_at);
  const toPay = shown.bookings.filter(b => b.owed_pence > 0);
  const results = q.trim() && !parseCode(q) ? searchDesk(shown, q) : [];
  const listed = (tab === 'arrived' ? arrived : shown.bookings.filter(b => !b.checked_in_at));

  return (
    <Shell title={shown.event.title} sub={`${dateTime(shown.event.starts_at)}${shown.event.venue ? ` · ${shown.event.venue}` : ''}`}>
      <div className="grid grid-cols-3 gap-2 text-center">
        <Stat label="Arrived" value={`${arrived.length} / ${shown.bookings.length}`} />
        <Stat label="To pay" value={String(toPay.length)} />
        <Stat label="Not booked yet" value={String(shown.members.length)} />
      </div>
      {(!online || queue.length > 0) && (
        <p className="flex items-center gap-2 rounded-sm bg-amber-50 px-3 py-2 text-sm text-amber-900"><WifiOff size={16} />
          {online ? `Sending ${queue.length}…` : `No signal. Check-ins are saved on this phone${queue.length ? ` (${queue.length} waiting to send)` : ''}. Taking money and booking need signal.`}</p>
      )}
      {toast && <p className="rounded-sm bg-emerald-50 px-3 py-2 text-[15px] font-semibold text-emerald-900" role="status">{toast}</p>}
      {error && <p className="rounded-sm bg-brand-50 px-3 py-2 text-[15px] text-brand-800" role="alert">{error}</p>}

      {current ? (
        <PersonCard found={current} online={online} busy={busy} onClose={() => { setFound(null); setError(''); }}
          onCheckIn={checkIn}
          onUndo={b => act(() => supabase.rpc('desk_undo_checkin', { p_registration: b.id }), `${b.name}: check-in undone`)}
          onSettle={(b, m) => act(() => supabase.rpc('desk_settle', { p_registration: b.id, p_method: m }), m === 'later' ? `${b.name} checked in, to pay later` : `${b.name}: ${money(b.owed_pence)} received, checked in`, () => b.id)}
          onBook={(m, optionId, method, label) => act(() => supabase.rpc('desk_book', { p_event: eventId, p_person: m.person_id, p_option: optionId, p_method: method }), `${m.name} booked on ${label} and checked in`, (id: string | null) => id)} />
      ) : (
        <>
          {scanning ? <Scanner onCode={onCode} onStop={() => setScanning(false)} /> : (
            <button className="btn-primary w-full py-5 text-lg" onClick={() => setScanning(true)}><Camera size={22} /> Scan a code</button>
          )}
          <label className="relative block">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input ref={inputRef} className="input pl-9" placeholder="Name or member number" value={q} autoComplete="off"
              onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onCode(q); } }} aria-label="Find someone" />
          </label>
          {results.length > 0 && (
            <ul className="card divide-y divide-stone-100 p-0">
              {results.map(f => <li key={f.kind === 'booking' ? f.booking.id : f.kind === 'member' ? f.member.person_id : 'x'}><button className="block w-full px-4 py-3 text-left hover:bg-stone-50" onClick={() => { setFound(f); setQ(''); }}><Line f={f} /></button></li>)}
            </ul>
          )}
          {q.trim() && !results.length && !parseCode(q) && <p className="text-sm text-ink-500">No one called “{q.trim()}”.</p>}
          {!q.trim() && (
            <section className="space-y-2">
              <div className="flex gap-2">
                {(['waiting', 'arrived'] as const).map(t => <button key={t} className={`btn-small ${tab === t ? 'border-ink-900' : ''}`} onClick={() => setTab(t)}>{t === 'waiting' ? `Not arrived (${shown.bookings.length - arrived.length})` : `Arrived (${arrived.length})`}</button>)}
              </div>
              <ul className="card divide-y divide-stone-100 p-0">
                {listed.map(b => <li key={b.id}><button className="block w-full px-4 py-2.5 text-left hover:bg-stone-50" onClick={() => setFound({ kind: 'booking', booking: b })}><Line f={{ kind: 'booking', booking: b }} /></button></li>)}
                {!listed.length && <li className="px-4 py-3 text-sm text-ink-500">{tab === 'arrived' ? 'No one yet.' : 'Everyone booked has arrived.'}</li>}
              </ul>
            </section>
          )}
        </>
      )}
    </Shell>
  );
}

function Shell({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-xl items-center gap-3 px-4 py-3">
          <Emblem size={34} />
          <div className="min-w-0 flex-1"><p className="eyebrow">Check-in desk</p><h1 className="truncate text-lg font-bold">{title}</h1>{sub && <p className="text-sm text-ink-500">{sub}</p>}</div>
          <Link to="/home" className="text-sm text-ink-500 hover:underline">Exit</Link>
        </div>
      </header>
      <main className="mx-auto max-w-xl space-y-4 px-4 py-4">{children}</main>
    </div>
  );
}

const Stat = ({ label, value }: { label: string; value: string }) => <div className="card px-2 py-2"><p className="text-xl font-bold">{value}</p><p className="text-xs text-ink-500">{label}</p></div>;

/** One line in a list: name, then what they are booked on or why not. */
function Line({ f }: { f: Found }) {
  if (f.kind === 'unknown') return null;
  if (f.kind === 'booking') {
    const b = f.booking; const s = bookingState(b);
    return <span className="flex items-center justify-between gap-2"><span><span className="font-semibold">{b.name}</span> <span className="text-sm text-ink-500">· {b.option}</span></span>
      <span className={`pill ${s === 'arrived' ? 'bg-emerald-100 text-emerald-800' : s === 'owes' ? 'bg-amber-100 text-amber-900' : 'bg-stone-100 text-ink-700'}`}>{s === 'arrived' ? `In ${time(b.checked_in_at!)}` : s === 'owes' ? `${money(b.owed_pence)} to pay` : 'Booked'}</span></span>;
  }
  const m = f.member;
  return <span className="flex items-center justify-between gap-2"><span><span className="font-semibold">{m.name}</span> <span className="text-sm text-ink-500">· {m.dojo ?? ''}</span></span><span className="pill bg-stone-100 text-ink-700">{m.reason ? 'Cannot book' : 'Not booked'}</span></span>;
}

function PersonCard({ found, online, busy, onClose, onCheckIn, onUndo, onSettle, onBook }: {
  found: Found; online: boolean; busy: boolean; onClose: () => void; onCheckIn: (b: DeskBooking) => void; onUndo: (b: DeskBooking) => void;
  onSettle: (b: DeskBooking, m: 'cash' | 'bank_transfer' | 'later') => void; onBook: (m: DeskMember, optionId: string, method: 'cash' | 'bank_transfer' | 'later', label: string) => void;
}) {
  const [option, setOption] = useState<{ id: string; name: string; price_pence: number } | null>(null);
  if (found.kind === 'unknown') return (
    <section className="card space-y-3 border-brand-600">
      <p className="text-lg font-bold">Code not recognised</p>
      <p className="text-[15px] text-ink-500">It is not a current EGKA member's code. Search by name instead.</p>
      <button className="btn-ghost w-full border border-stone-300" onClick={onClose}>Back</button>
    </section>
  );
  const who = found.kind === 'booking' ? found.booking : found.member;
  const tone = found.kind === 'booking' ? { arrived: 'border-emerald-600', owes: 'border-amber-500', ready: 'border-emerald-600' }[bookingState(found.booking)] : found.member.reason ? 'border-brand-600' : 'border-stone-400';
  const methods = [['cash', 'Paid cash'], ['bank_transfer', 'Bank transfer'], ['later', 'Pay later']] as const;
  const needSignal = !online && <p className="text-sm text-amber-900">Needs signal. Check them in now and settle the money later, or wait for signal.</p>;

  return (
    <section className={`card space-y-4 border-l-4 ${tone}`}>
      <div className="flex items-start gap-3">
        <Avatar path={who.photo_path} name={who.name} size={64} />
        <div className="min-w-0 flex-1">
          <p className="text-xl font-bold">{who.name}</p>
          <p className="text-sm text-ink-500">{[band(who.band), who.dojo, who.member_number != null && `No. ${who.member_number}`].filter(Boolean).join(' · ')}</p>
          {who.band && who.band !== 'adult' && !who.photo_consent && <p className="mt-1 inline-block rounded-sm bg-brand-600 px-2 py-0.5 text-xs font-bold text-white">No event photos</p>}
        </div>
      </div>

      {found.kind === 'booking' && (() => {
        const b = found.booking; const s = bookingState(b);
        return (
          <div className="space-y-3">
            <p className="text-[15px]">Booked: <strong>{b.option}</strong>{b.price_pence > 0 && <span className="text-ink-500"> · {money(b.price_pence)}</span>}</p>
            {b.desk_note && <p className="text-sm text-ink-500">{b.desk_note}</p>}
            {s === 'arrived' && <>
              <p className="flex items-center gap-2 text-lg font-bold text-emerald-800"><Check size={20} /> Arrived {time(b.checked_in_at!)}{b.checked_in_by && <span className="text-sm font-normal text-ink-500">, checked in by {b.checked_in_by}</span>}</p>
              {b.owed_pence > 0 && <p className="font-semibold text-amber-900">Still {money(b.owed_pence)} to pay.</p>}
              {b.owed_pence > 0 && online && <div className="grid grid-cols-2 gap-2">{methods.filter(([m]) => m !== 'later').map(([m, l]) => <button key={m} className="btn-ghost border border-stone-300" disabled={busy} onClick={() => onSettle(b, m)}>{l}</button>)}</div>}
              <button className="btn-small" disabled={busy || !online} onClick={() => onUndo(b)}><RotateCcw size={13} /> Undo check-in</button>
            </>}
            {s === 'ready' && <button className="btn-primary w-full py-4 text-lg" disabled={busy} onClick={() => onCheckIn(b)}><Check size={20} /> Check in</button>}
            {s === 'owes' && <>
              <p className="text-lg font-bold text-amber-900">{money(b.owed_pence)} to pay</p>
              <div className="grid grid-cols-3 gap-2">{methods.map(([m, l]) => <button key={m} className={m === 'cash' ? 'btn-primary' : 'btn-ghost border border-stone-300'} disabled={busy || !online} onClick={() => onSettle(b, m)}>{l}</button>)}</div>
              {needSignal}
              {!online && <button className="btn-ghost w-full border border-stone-300" onClick={() => onCheckIn(b)}>Check in, money still owed</button>}
            </>}
          </div>
        );
      })()}

      {found.kind === 'member' && (() => {
        const m = found.member;
        if (m.reason) return <p className="font-semibold text-brand-700">{reasonText(m.reason)}</p>;
        if (!m.options.length) return <p className="font-semibold text-brand-700">No option on this event fits them.</p>;
        return (
          <div className="space-y-3">
            <p className="text-[15px] font-semibold">Not booked. Book them on:</p>
            <div className="grid gap-2">{m.options.map(o => <button key={o.id} className={`btn-ghost justify-between border ${option?.id === o.id ? 'border-ink-900 bg-stone-100' : 'border-stone-300'}`} onClick={() => setOption(o)}><span>{o.name}</span><span>{o.price_pence ? money(o.price_pence) : 'Free'}</span></button>)}</div>
            {option && (option.price_pence === 0
              ? <button className="btn-primary w-full py-4" disabled={busy || !online} onClick={() => onBook(m, option.id, 'cash', option.name)}>Book and check in</button>
              : <div className="grid grid-cols-3 gap-2">{methods.map(([k, l]) => <button key={k} className={k === 'cash' ? 'btn-primary' : 'btn-ghost border border-stone-300'} disabled={busy || !online} onClick={() => onBook(m, option.id, k, option.name)}>{l}</button>)}</div>)}
            {needSignal}
          </div>
        );
      })()}

      <button className="btn-ghost w-full border border-stone-300" onClick={onClose}>Next person</button>
    </section>
  );
}

/** The phone's camera, read for QR codes a few times a second. */
function Scanner({ onCode, onStop }: { onCode: (text: string) => void; onStop: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [problem, setProblem] = useState('');
  const handler = useRef(onCode);
  handler.current = onCode;   // the latest handler, without restarting the camera
  useEffect(() => {
    let stream: MediaStream | null = null; let timer = 0; let stopped = false; let last = ''; let lastAt = 0;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
        if (stopped) { stream.getTracks().forEach(t => t.stop()); return; }
        video.current!.srcObject = stream; await video.current!.play();
        const tick = () => {
          const v = video.current, c = canvas.current;
          if (v && c && v.readyState === v.HAVE_ENOUGH_DATA) {
            const w = 480, h = Math.round((v.videoHeight / v.videoWidth) * 480) || 360;
            c.width = w; c.height = h;
            const ctx = c.getContext('2d', { willReadFrequently: true })!;
            ctx.drawImage(v, 0, 0, w, h);
            const hit = jsQR(ctx.getImageData(0, 0, w, h).data, w, h, { inversionAttempts: 'dontInvert' });
            // The same code held in view is read once, not every frame.
            if (hit?.data && (hit.data !== last || Date.now() - lastAt > 4000)) { last = hit.data; lastAt = Date.now(); handler.current(hit.data); }
          }
          timer = window.setTimeout(tick, 150);
        };
        tick();
      } catch {
        setProblem('The camera could not start. Allow camera access for this site in your phone settings, or search by name.');
      }
    })();
    return () => { stopped = true; clearTimeout(timer); stream?.getTracks().forEach(t => t.stop()); };
  }, []);
  return (
    <div className="space-y-2">
      {problem ? <p className="card text-[15px] text-brand-800">{problem}</p> : (
        <div className="relative overflow-hidden rounded-sm bg-ink-900">
          <video ref={video} className="aspect-[4/3] w-full object-cover" playsInline muted />
          <div className="pointer-events-none absolute inset-[18%] rounded-sm border-2 border-white/80" />
        </div>
      )}
      <canvas ref={canvas} className="hidden" />
      <button className="btn-ghost w-full border border-stone-300" onClick={onStop}><CameraOff size={18} /> Stop camera</button>
    </div>
  );
}
